import { supabase } from '../lib/supabase';

// CRITICAL FIX: Get effective user ID for all queries
const getEffectiveUserId = async () => {
  // Check for impersonation first
  const impersonationData = localStorage.getItem('admin_impersonation');
  if (impersonationData) {
    const { impersonatedUserId } = JSON.parse(impersonationData);
    return impersonatedUserId;
  }
  
  // Fall back to authenticated user
  const { data: { user } } = await supabase?.auth?.getUser();
  return user?.id || null;
};

export const projectTaskService = {
  // Get all tasks for current user
  async getAll() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('project_tasks')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      taskName: row?.task_name,
      description: row?.description,
      dueDate: row?.due_date,
      status: row?.status,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Get tasks by status (exclude archived tasks)
  async getByStatus(status) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('project_tasks')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.eq('status', status)
      ?.is('archived_at', null)  // Explicitly exclude archived tasks
      ?.order('due_date', { ascending: true });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      taskName: row?.task_name,
      description: row?.description,
      dueDate: row?.due_date,
      status: row?.status,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Create new task
  async create(taskData) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('project_tasks')?.insert({
        user_id: userId,
        task_name: taskData?.taskName,
        description: taskData?.description,
        due_date: taskData?.dueDate,
        status: taskData?.status || 'to_do'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      taskName: data?.task_name,
      description: data?.description,
      dueDate: data?.due_date,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update task
  async update(taskId, updates) {
    const updateData = {};
    
    if (updates?.taskName !== undefined) updateData.task_name = updates?.taskName;
    if (updates?.description !== undefined) updateData.description = updates?.description;
    if (updates?.dueDate !== undefined) updateData.due_date = updates?.dueDate;
    if (updates?.status !== undefined) updateData.status = updates?.status;

    const { data, error } = await supabase?.from('project_tasks')?.update(updateData)?.eq('id', taskId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      taskName: data?.task_name,
      description: data?.description,
      dueDate: data?.due_date,
      status: data?.status,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete task
  async delete(taskId) {
    const { error } = await supabase?.from('project_tasks')?.delete()?.eq('id', taskId);

    if (error) throw error;
    return true;
  },

  // Get archived tasks for current user
  async getArchived() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('project_tasks')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.not('archived_at', 'is', null)
      ?.order('archived_at', { ascending: false });

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      taskName: row?.task_name,
      description: row?.description,
      dueDate: row?.due_date,
      status: row?.status,
      archivedAt: row?.archived_at,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      daysUntilDeletion: row?.archived_at 
        ? Math.max(0, 30 - Math.floor((new Date() - new Date(row.archived_at)) / (1000 * 60 * 60 * 24)))
        : null
    }));
  },

  // Archive a completed task
  async archive(taskId) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    // First check if task is completed
    const { data: task } = await supabase
      ?.from('project_tasks')
      ?.select('status')
      ?.eq('id', taskId)
      ?.eq('user_id', userId)
      ?.single();

    if (task?.status !== 'completed') {
      throw new Error('Only completed tasks can be archived');
    }

    const { data, error } = await supabase
      ?.from('project_tasks')
      ?.update({ archived_at: new Date()?.toISOString() })
      ?.eq('id', taskId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      taskName: data?.task_name,
      description: data?.description,
      dueDate: data?.due_date,
      status: data?.status,
      archivedAt: data?.archived_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Restore an archived task
  async restore(taskId) {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('project_tasks')
      ?.update({ archived_at: null })
      ?.eq('id', taskId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      taskName: data?.task_name,
      description: data?.description,
      dueDate: data?.due_date,
      status: data?.status,
      archivedAt: data?.archived_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Get task statistics (updated to exclude archived tasks)
  async getStatistics() {
    const userId = await getEffectiveUserId();
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('project_tasks')
      ?.select('status, archived_at')
      ?.eq('user_id', userId);

    if (error) throw error;

    // Filter out archived tasks
    const activeTasks = data?.filter(t => !t?.archived_at) || [];
    const archivedTasks = data?.filter(t => t?.archived_at) || [];

    const stats = {
      total: activeTasks?.length,
      toDo: activeTasks?.filter(t => t?.status === 'to_do')?.length,
      inProgress: activeTasks?.filter(t => t?.status === 'in_progress')?.length,
      completed: activeTasks?.filter(t => t?.status === 'completed')?.length,
      archived: archivedTasks?.length
    };

    return stats;
  }
};